/**
 * @packageDocumentation
 * InstructionScreen (match conditions overlay)
 *
 * This version fixes fan sizing issues by:
 *  1) Building each fan in a normalized local coordinate space (no 1000px child offsets).
 *  2) Applying all fan/card spacing + rotation knobs BEFORE fitting to Figma LTWH.
 *  3) Fitting to the Figma LTWH box, then applying an optional scale multiplier (per orientation, per fan).
 *
 * Result: you can reliably control:
 *  - fan scale (per fan, portrait/landscape)
 *  - spacing between cards (per fan, portrait/landscape)
 *  - rotation of each card (per fan, per card)
 *  - rotation of each fan container
 */

import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import { gsap } from "gsap/gsap-core";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { HandleOrientation } from "./Game/global";

type FanKey = "L" | "R" | "B";

type FanTune = {
  /** Additional scaling after Figma fit (1 = exact fit). */
  scaleMul: number;

  /** Fan container rotation (degrees). */
  fanRotDeg: number;

  /**
   * Horizontal spacing between cards.
   * This is the "spread" control you asked for.
   * 1.0 = baseline spacing; >1 spreads apart; <1 pulls together.
   */
  spacingMul: number;

  /**
   * Per-card rotation degrees (left, middle, right).
   * This is applied to each pairContainer (not the fan container).
   */
  cardRotDeg: [number, number, number];

  /**
   * Optional per-card X nudge (in local fan space), after spacing.
   * Useful if you want asymmetry.
   */
  cardXNudge?: [number, number, number];

  /**
   * Optional per-card Y nudge (in local fan space).
   * Useful if you want one card higher/lower.
   */
  cardYNudge?: [number, number, number];
};

export class InstructionScreen extends PIXI.Container {
  // -----------------------------
  // Scene graph
  // -----------------------------

  /** Figma-scaled root. Everything lives under here. */
  private designRoot = new PIXI.Container();

  /** Bubble background sprite */
  private instructionBg!: PIXI.Sprite;
  /** Top logo sprite */
  private logoSprite!: PIXI.Sprite;
  /** CTA text */
  private downText!: PIXI.Text;

  /** Purple text cards */
  private inst1 = new PIXI.Container();
  private inst2 = new PIXI.Container();
  private inst3 = new PIXI.Container();

  /** Fans (each holds 3 card+frame pairs) */
  private fanLeft = new PIXI.Container();
  private fanRight = new PIXI.Container();
  private fanBottom = new PIXI.Container();

  private parrotAnim?: Spine;
  private circleBox?: PIXI.Container;

  // Resize handler bookkeeping
  private raf = 0;
  private onResizeBound = () => this.requestLayout();

  // -----------------------------
  // Figma source of truth (Portrait)
  // -----------------------------
  private readonly FIGMA_P = {
    BASE_W: 1080,
    BASE_H: 1920,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    LOGO: { left: 342, top: 319, w: 368, h: 238 },
    ACTION_BUBBLE: { left: -257, top: 385, w: 1558, h: 786 },

    MATCH_CHARACTERS: { left: 37, top: 500, w: 340.2, h: 211.69 },
    MATCH_BORDERS: { left: 638, top: 640, w: 331.55, h: 206.09 },
    THREE_VILLAINS: { left: 313.08, top: 1060, w: 474.95, h: 265.03 },

    TEXTBOX_1: { left: 236, top: 583, w: 425, h: 133 },
    TEXTBOX_2: { left: 449, top: 748.36, w: 425, h: 133 },
    TEXTBOX_3: { left: 301.35, top: 916.86, w: 498.4, h: 155.97 },

    CTA_TEXT: { left: 304, top: 1388, w: 471, h: 108 },
  } as const;

  // -----------------------------
  // Figma source of truth (Landscape)
  // -----------------------------
  private readonly FIGMA_L = {
    BASE_W: 1920,
    BASE_H: 1080,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    ACTION_BUBBLE: { left: 355, top: 149, w: 1187, h: 599 },

    LOGO: { left: 742, top: 54, w: 412, h: 267 },

    MATCH_CHARACTERS: { left: 360, top: 250, w: 340, h: 212, rotDeg: -38.83 },
    MATCH_BORDERS: { left: 1160, top: 250, w: 340, h: 212, rotDeg: 38.04 },
    THREE_VILLAINS: { left: 758, top: 612, w: 405, h: 226, rotDeg: 0 },

    TEXTBOX_1: { left: 505, top: 333, w: 425, h: 133 },
    TEXTBOX_2: { left: 984, top: 333, w: 425, h: 133 },
    TEXTBOX_3: { left: 748, top: 505, w: 425, h: 133 },

    CTA_TEXT: { left: 725, top: 852, w: 471, h: 108 },
  } as const;

  // -----------------------------
  // Fan tuning knobs (EDIT THESE)
  // -----------------------------
  /**
   * These are the knobs you asked for.
   * Adjust these values and your fans/cards will respond predictably.
   */
  private readonly FAN_TUNE_P: Record<FanKey, FanTune> = {
    L: {
      scaleMul: 1.2,
      fanRotDeg: -38,
      spacingMul:0.6,
      cardRotDeg: [-10, 0, 10],
    },
    R: {
      scaleMul: 1.2,
      fanRotDeg: 38,
      spacingMul: 0.6,
      cardRotDeg: [-10, 0, 10],
    },
    B: {
      scaleMul: 1.2,
      fanRotDeg: 0,
      spacingMul: 1,
      cardRotDeg: [2, 0, -2],
    },
  };

  private readonly FAN_TUNE_L: Record<FanKey, FanTune> = {
    L: {
      scaleMul: 1.2,
      fanRotDeg: this.FIGMA_L.MATCH_CHARACTERS.rotDeg,
      spacingMul: 1.0,
      cardRotDeg: [-10, 0, 10],
    },
    R: {
      scaleMul: 1.2,
      fanRotDeg: this.FIGMA_L.MATCH_BORDERS.rotDeg,
      spacingMul: 1.0,
      cardRotDeg: [-10, 0, 10],
    },
    B: {
      scaleMul: 1.2,
      fanRotDeg: this.FIGMA_L.THREE_VILLAINS.rotDeg,
      spacingMul: 1.0,
      cardRotDeg: [-10, 0, 10],
    },
  };

  // -----------------------------
  // Fan baseline geometry (do not use screen-space values here)
  // -----------------------------
  private readonly FAN_BASE = {
    /** Baseline card spacing in local fan space (before spacingMul). */
    CARD_SPACING_X: 90,

    /** Baseline Y offset for the three cards (all start at 0). */
    CARD_BASE_Y: 0,
  } as const;

  constructor(private app: PIXI.Application, private onContinue: () => void) {
    super();
    this.sortableChildren = true;

    this.designRoot.sortableChildren = true;
    this.addChild(this.designRoot);

    // Build fixed UI
    this.createInstructionBg();
    this.createInstructionScreenLogo();
    this.createCTA();

    // Build instruction cards + fans
    this.createInstructionCards();
    this.buildFansNormalized();

    // Optional extras (not present in provided Figma specs)
    this.createParrotAnim();
    this.createCircleBox();

    // Interaction
    this.eventMode = "static";
    this.cursor = "pointer";
    this.on("pointertap", () => this.onContinue());

    // Orientation hooks + resize listeners
    HandleOrientation(this.PortraitAdjustFun.bind(this), this.landScapeAdjustFun.bind(this));
    this.addLayoutListeners();

    // Initial layout
    this.applyResponsiveLayout();
  }

  // ---------------------------------------------------------------------------
  // Screen size + Figma placement helpers
  // ---------------------------------------------------------------------------

  private getScreenSize() {
    const screenW = this.app?.renderer?.screen?.width ?? (this.parent as any)?.width ?? window.innerWidth;
    const screenH = this.app?.renderer?.screen?.height ?? (this.parent as any)?.height ?? window.innerHeight;
    return { screenW, screenH };
  }

  private layoutDesignRoot(
    screenW: number,
    screenH: number,
    cfg: { BASE_W: number; BASE_H: number; ROOT_OFFSET_X: number; ROOT_OFFSET_Y: number }
  ) {
    const s = Math.min(screenW / cfg.BASE_W, screenH / cfg.BASE_H);
    this.designRoot.scale.set(s);
    this.designRoot.x = (screenW - cfg.BASE_W * s) / 2 + cfg.ROOT_OFFSET_X;
    this.designRoot.y = (screenH - cfg.BASE_H * s) / 2 + cfg.ROOT_OFFSET_Y;
    return s;
  }

  private figmaCenter(left: number, top: number, w: number, h: number) {
    return { x: left + w / 2, y: top + h / 2 };
  }

  private placeFromFigmaLTWH(
    obj: PIXI.Container,
    left: number,
    top: number,
    w: number,
    h: number,
    opts?: { fitToSize?: boolean; nudgeX?: number; nudgeY?: number }
  ) {
    const { fitToSize = false, nudgeX = 0, nudgeY = 0 } = opts ?? {};

    if (fitToSize && (obj as any).width != null && (obj as any).height != null) {
      const anyObj = obj as any as { width: number; height: number; scale: PIXI.ObservablePoint };
      if (anyObj.width > 0 && anyObj.height > 0) {
        const sx = w / anyObj.width;
        const sy = h / anyObj.height;
        anyObj.scale.set(anyObj.scale.x * sx, anyObj.scale.y * sy);
      }
    }

    const b = obj.getLocalBounds();
    obj.pivot.set(b.x + b.width / 2, b.y + b.height / 2);

    const c = this.figmaCenter(left, top, w, h);
    obj.position.set(c.x + nudgeX, c.y + nudgeY);
  }

  /**
   * Fit container to target LTWH and then apply scaleMul (explicit knob).
   * NOTE: This sets container.scale directly; do not set scale elsewhere.
   */
  private fitContainerToFigmaLTWH(
    container: PIXI.Container,
    target: { left: number; top: number; w: number; h: number },
    opts?: { cover?: boolean; nudgeX?: number; nudgeY?: number; scaleMul?: number }
  ) {
    const { cover = false, nudgeX = 0, nudgeY = 0, scaleMul = 1 } = opts ?? {};
    const b = container.getLocalBounds();
    if (b.width < 1 || b.height < 1) return false;

    const sx = target.w / b.width;
    const sy = target.h / b.height;
    const base = cover ? Math.max(sx, sy) : Math.min(sx, sy);
    const s = base * scaleMul;

    container.scale.set(s);
    container.pivot.set(b.x + b.width / 2, b.y + b.height / 2);
    container.position.set(target.left + target.w / 2 + nudgeX, target.top + target.h / 2 + nudgeY);
    return true;
  }

  private requestLayout() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.applyResponsiveLayout());
  }

  private addLayoutListeners() {
    window.addEventListener("resize", this.onResizeBound);
    window.addEventListener("orientationchange", this.onResizeBound);
    window.visualViewport?.addEventListener("resize", this.onResizeBound);
    window.visualViewport?.addEventListener("scroll", this.onResizeBound);
  }

  private removeLayoutListeners() {
    window.removeEventListener("resize", this.onResizeBound);
    window.removeEventListener("orientationchange", this.onResizeBound);
    window.visualViewport?.removeEventListener("resize", this.onResizeBound);
    window.visualViewport?.removeEventListener("scroll", this.onResizeBound);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private applyResponsiveLayout() {
    const { screenW, screenH } = this.getScreenSize();
    const portrait = screenH > screenW;
    if (portrait) this.applyPortraitLayout(screenW, screenH);
    else this.applyLandscapeLayout(screenW, screenH);
  }

  // ---------------------------------------------------------------------------
  // Build fixed UI
  // ---------------------------------------------------------------------------

  private createInstructionBg() {
    const bg = new PIXI.Sprite(Assets.get("startPage_popup"));
    bg.anchor.set(0.5);
    bg.zIndex = 0;
    this.designRoot.addChild(bg);
    this.instructionBg = bg;
  }

  private createInstructionScreenLogo() {
    const logo = new PIXI.Sprite(Assets.get("startPage_logo"));
    logo.anchor.set(0.5);
    logo.zIndex = 10;
    this.designRoot.addChild(logo);
    this.logoSprite = logo;
  }

  private createCTA() {
    const downText = new PIXI.Text({
      text: "CLICK TO PLAY",
      style: new PIXI.TextStyle({
        fill: "#FFFFFF",
        fontSize: 125,
        fontFamily: "RoadRage",
        fontWeight: "400",
        align: "center",
        dropShadow: true,
        dropShadowColor: "#000000",
        dropShadowAlpha: 0.5,
        dropShadowBlur: 4,
        dropShadowAngle: Math.atan2(10, -10),
        dropShadowDistance: Math.hypot(10, 10),
      }),
    });
    downText.anchor.set(0.5);
    downText.zIndex = 20;
    this.designRoot.addChild(downText);
    this.downText = downText;

    gsap.to(downText, { pixi: { scale: 0.9 }, duration: 1, repeat: -1, yoyo: true });
  }

  // ---------------------------------------------------------------------------
  // Instruction cards
  // ---------------------------------------------------------------------------

  private buildInstructionCard(
    container: PIXI.Container,
    opts: { bgKey: string; text: string; wrapW: number; fontSize: number; lineHeight: number; textNudgeY?: number }
  ) {
    container.removeChildren();
    container.sortableChildren = true;

    const card = new PIXI.Sprite(Assets.get(opts.bgKey));
    card.anchor.set(0.5);
    card.zIndex = 0;
    container.addChild(card);

    const text = new PIXI.Text({
      text: opts.text,
      style: new PIXI.TextStyle({
        fontFamily: "PermanentMarker",
        fontSize: opts.fontSize,
        fontWeight: "400",
        fill: "#FFFFFF",
        align: "center",
        lineHeight: opts.lineHeight,
        letterSpacing: 0,
        wordWrap: true,
        wordWrapWidth: opts.wrapW,
      }),
    });

    text.anchor.set(0.5);
    text.zIndex = 10;
    text.y = opts.textNudgeY ?? 0;
    container.addChild(text);
  }

  private createInstructionCards() {
    this.buildInstructionCard(this.inst1, {
      bgKey: "startPage_card",
      text: "COLLECT \nCHARACTERS",
      wrapW: 420,
      fontSize: 34,
      lineHeight: 40,
    });

    this.buildInstructionCard(this.inst3, {
      bgKey: "startPage_card",
      text: "MATCH \nBORDERS",
      wrapW: 420,
      fontSize: 34,
      lineHeight: 40,
    });

    this.buildInstructionCard(this.inst2, {
      bgKey: "landing_bg_box_big",
      text: "3 VILLAINS\n = \nBONUS GAME",
      wrapW: 470,
      fontSize: 34,
      lineHeight: 34,
      textNudgeY: -8,
    });

    this.inst1.zIndex = 15;
    this.inst2.zIndex = 15;
    this.inst3.zIndex = 15;

    this.designRoot.addChild(this.inst1);
    this.designRoot.addChild(this.inst2);
    this.designRoot.addChild(this.inst3);
  }

  // ---------------------------------------------------------------------------
  // Extras (optional)
  // ---------------------------------------------------------------------------

  private createParrotAnim(): void {
    const parrotAnim = Spine.from({ skeleton: "parrot_anim_json", atlas: "parrot_anim_atlas", scale: 2 });
    parrotAnim.rotation = (13 * Math.PI) / 180;
    parrotAnim.state.setAnimation(0, "idle_anim", true);
    parrotAnim.state.timeScale = 0.8;
    parrotAnim.scale.set(0.25);
    parrotAnim.zIndex = 30;
    this.designRoot.addChild(parrotAnim);
    this.parrotAnim = parrotAnim;
  }

  private createCircleBox() {
    const boxContainer = new PIXI.Container();
    const boxSprite = new PIXI.Sprite(Assets.get("lp_circle"));
    boxSprite.anchor.set(0.5);
    boxContainer.addChild(boxSprite);
    boxContainer.zIndex = 12;
    this.designRoot.addChild(boxContainer);
    this.circleBox = boxContainer;
  }

  private degToRad(deg: number) {
    return (deg * Math.PI) / 180;
  }

  // ---------------------------------------------------------------------------
  // Fans (NORMALIZED build + tune knobs)
  // ---------------------------------------------------------------------------

  /**
   * Build all three fans in local space (centered around 0,0).
   * No absolute 1000px positions inside the fan containers.
   */
  private buildFansNormalized() {
    this.fanLeft.removeChildren();
    this.fanRight.removeChildren();
    this.fanBottom.removeChildren();

    this.fanLeft.sortableChildren = true;
    this.fanRight.sortableChildren = true;
    this.fanBottom.sortableChildren = true;

    // Left fan (match characters)
    this.buildFan3(this.fanLeft, [
      {
        frame: { skeleton: "bronze_frame_anim_json", atlas: "bronze_frame_anim_atlas" },
        hero: { skeleton: "pink_civilian_json", atlas: "pink_civilian_atlas" },
        heroScale: 0.15,
        heroNudge: { x: 0, y: 0 },
      },
      {
        frame: { skeleton: "Silver_frame_anim_json", atlas: "Silver_frame_anim_atlas" },
        hero: { skeleton: "pink_villain_anim_json", atlas: "pink_villain_anim_atlas" },
        heroScale: 0.15,
        heroNudge: { x: -5, y: 10 },
      },
      {
        frame: { skeleton: "Gold_frame_anim_json", atlas: "Gold_frame_anim_atlas" },
        hero: { skeleton: "pink_civilian_json", atlas: "pink_civilian_atlas" },
        heroScale: 0.15,
        heroNudge: { x: 0, y: 0 },
      },
    ]);

    // Right fan (match borders)
    this.buildFan3(this.fanRight, [
      {
        frame: { skeleton: "Hologram_frame_anim_json", atlas: "Hologram_frame_anim_atlas" },
        hero: { skeleton: "teal_civilian_anim_json", atlas: "teal_civilian_anim_atlas" },
        heroScale: 0.15,
        heroNudge: { x: 0, y: 0 },
      },
      {
        frame: { skeleton: "Hologram_frame_anim_json", atlas: "Hologram_frame_anim_atlas" },
        hero: { skeleton: "orange_civilian_anim_json", atlas: "orange_civilian_anim_atlas" },
        heroScale: 0.15,
        heroNudge: { x: 0, y: 0 },
      },
      {
        frame: { skeleton: "Hologram_frame_anim_json", atlas: "Hologram_frame_anim_atlas" },
        hero: { skeleton: "rainbow_civilian_anim_json", atlas: "rainbow_civilian_anim_atlas" },
        heroScale: 0.15,
        heroNudge: { x: 0, y: 0 },
      },
    ]);

    // Bottom fan (3 villains = bonus)
    this.buildFan3(this.fanBottom, [
      {
        frame: { skeleton: "bronze_frame_anim_json", atlas: "bronze_frame_anim_atlas" },
        hero: { skeleton: "teal_villain_anim_json", atlas: "teal_villain_anim_atlas" },
        heroScale: 0.15,
        heroNudge: { x: 0, y: 0 },
      },
      {
        frame: { skeleton: "Silver_frame_anim_json", atlas: "Silver_frame_anim_atlas" },
        hero: { skeleton: "orange_villain_anim_json", atlas: "orange_villain_anim_atlas" },
        heroScale: 0.15,
        heroNudge: { x: 0, y: 0 },
      },
      {
        frame: { skeleton: "Gold_frame_anim_json", atlas: "Gold_frame_anim_atlas" },
        hero: { skeleton: "white_villain_anim_json", atlas: "white_villain_anim_atlas" },
        heroScale: 0.15,
        heroNudge: { x: 0, y: 0 },
      },
    ]);

    // Add to scene (zIndex handled in layout)
    this.designRoot.addChild(this.fanLeft);
    this.designRoot.addChild(this.fanRight);
    this.designRoot.addChild(this.fanBottom);
  }

  private buildFan3(
    fan: PIXI.Container,
    items: Array<{
      frame: { skeleton: string; atlas: string };
      hero: { skeleton: string; atlas: string };
      heroScale: number;
      heroNudge?: { x: number; y: number };
    }>
  ) {
    const frameY = -68;

    for (let i = 0; i < 3; i++) {
      const cfg = items[i];

      const pair = new PIXI.Container();
      pair.name = `pair_${i}`;
      pair.sortableChildren = true;

      const hero = Spine.from({ skeleton: cfg.hero.skeleton, atlas: cfg.hero.atlas, scale: 2 });
      hero.scale.set(cfg.heroScale);
      hero.state.setAnimation(0, "idle_anim", true);
      hero.state.timeScale = 0.8;
      hero.x = cfg.heroNudge?.x ?? 0;
      hero.y = cfg.heroNudge?.y ?? 0;
      hero.zIndex = 0;
      pair.addChild(hero);

      const frame = Spine.from({ skeleton: cfg.frame.skeleton, atlas: cfg.frame.atlas, scale: 2 });
      frame.scale.set(cfg.heroScale);
      frame.y = frameY;
      frame.state.setAnimation(0, "static", true);
      frame.state.timeScale = 0.8;
      frame.zIndex = 10;
      pair.addChild(frame);

      fan.addChild(pair);
    }
  }

  /**
   * Apply spacing + per-card rotations to a fan BEFORE fitting.
   * This is where you adjust spacing/rotation behavior.
   */
  private applyFanTune(fan: PIXI.Container, tune: FanTune) {
    const kids = fan.children as PIXI.Container[];
    if (kids.length !== 3) return;

    const baseX = this.FAN_BASE.CARD_SPACING_X * tune.spacingMul;
    const xs = [-baseX, 0, baseX];

    for (let i = 0; i < 3; i++) {
      const k = kids[i];
      k.x = xs[i] + (tune.cardXNudge?.[i] ?? 0);
      k.y = this.FAN_BASE.CARD_BASE_Y + (tune.cardYNudge?.[i] ?? 0);
      k.rotation = this.degToRad(tune.cardRotDeg[i]);
    }

    // Fan rotation happens at the fan container level
    fan.rotation = this.degToRad(tune.fanRotDeg);
  }

  // ---------------------------------------------------------------------------
  // Layouts
  // ---------------------------------------------------------------------------

  private applyPortraitLayout(screenW: number, screenH: number) {
    this.layoutDesignRoot(screenW, screenH, this.FIGMA_P);

    // Match portrait Figma: hide extras
    if (this.parrotAnim) this.parrotAnim.visible = false;
    if (this.circleBox) this.circleBox.visible = false;

    // Layering
    this.instructionBg.zIndex = 0;
    this.logoSprite.zIndex = 10;
    this.inst1.zIndex = 15;
    this.inst2.zIndex = 15;
    this.inst3.zIndex = 15;
    this.fanLeft.zIndex = 18;
    this.fanRight.zIndex = 18;
    this.fanBottom.zIndex = 18;
    this.downText.zIndex = 20;

    // Bubble
    this.instructionBg.scale.set(0.8);
    this.placeFromFigmaLTWH(
      this.instructionBg as any,
      this.FIGMA_P.ACTION_BUBBLE.left,
      this.FIGMA_P.ACTION_BUBBLE.top,
      this.FIGMA_P.ACTION_BUBBLE.w,
      this.FIGMA_P.ACTION_BUBBLE.h,
      { fitToSize: true }
    );

    // Logo
    this.logoSprite.scale.set(1);
    this.placeFromFigmaLTWH(
      this.logoSprite as any,
      this.FIGMA_P.LOGO.left,
      this.FIGMA_P.LOGO.top,
      this.FIGMA_P.LOGO.w,
      this.FIGMA_P.LOGO.h,
      { fitToSize: true }
    );

    // Instruction cards
    this.inst1.scale.set(1);
    this.placeFromFigmaLTWH(this.inst1, this.FIGMA_P.TEXTBOX_1.left, this.FIGMA_P.TEXTBOX_1.top, this.FIGMA_P.TEXTBOX_1.w, this.FIGMA_P.TEXTBOX_1.h, {
      fitToSize: true,
    });

    this.inst3.scale.set(1);
    this.placeFromFigmaLTWH(this.inst3, this.FIGMA_P.TEXTBOX_2.left, this.FIGMA_P.TEXTBOX_2.top, this.FIGMA_P.TEXTBOX_2.w, this.FIGMA_P.TEXTBOX_2.h, {
      fitToSize: true,
    });

    this.inst2.scale.set(1);
    this.placeFromFigmaLTWH(this.inst2, this.FIGMA_P.TEXTBOX_3.left, this.FIGMA_P.TEXTBOX_3.top, this.FIGMA_P.TEXTBOX_3.w, this.FIGMA_P.TEXTBOX_3.h, {
      fitToSize: true,
    });

    // ---- Fans: tune first (spacing/rot), then fit with scaleMul knob ----
    this.applyFanTune(this.fanLeft, this.FAN_TUNE_P.L);
    this.applyFanTune(this.fanRight, this.FAN_TUNE_P.R);
    this.applyFanTune(this.fanBottom, this.FAN_TUNE_P.B);

    const okL = this.fitContainerToFigmaLTWH(this.fanLeft, this.FIGMA_P.MATCH_CHARACTERS, { scaleMul: this.FAN_TUNE_P.L.scaleMul });
    const okR = this.fitContainerToFigmaLTWH(this.fanRight, this.FIGMA_P.MATCH_BORDERS, { scaleMul: this.FAN_TUNE_P.R.scaleMul });
    const okB = this.fitContainerToFigmaLTWH(this.fanBottom, this.FIGMA_P.THREE_VILLAINS, { scaleMul: this.FAN_TUNE_P.B.scaleMul });

    if (!okL || !okR || !okB) requestAnimationFrame(() => this.requestLayout());

    // CTA
    this.downText.scale.set(1);
    this.placeFromFigmaLTWH(
      this.downText as any,
      this.FIGMA_P.CTA_TEXT.left,
      this.FIGMA_P.CTA_TEXT.top,
      this.FIGMA_P.CTA_TEXT.w,
      this.FIGMA_P.CTA_TEXT.h,
      { fitToSize: false }
    );

    this.designRoot.sortChildren();
  }

  private applyLandscapeLayout(screenW: number, screenH: number) {
    // Hide extras unless specified
    if (this.parrotAnim) this.parrotAnim.visible = false;
    if (this.circleBox) this.circleBox.visible = false;

    this.layoutDesignRoot(screenW, screenH, this.FIGMA_L);

    // Layering
    this.instructionBg.zIndex = 0;
    this.logoSprite.zIndex = 10;
    this.inst1.zIndex = 12;
    this.inst2.zIndex = 12;
    this.inst3.zIndex = 12;
    this.fanLeft.zIndex = 13;
    this.fanRight.zIndex = 13;
    this.fanBottom.zIndex = 13;
    this.downText.zIndex = 20;

    // Bubble (no extra scale hacks; LTWH controls size)
    this.instructionBg.scale.set(1);
    this.placeFromFigmaLTWH(
      this.instructionBg as any,
      this.FIGMA_L.ACTION_BUBBLE.left,
      this.FIGMA_L.ACTION_BUBBLE.top,
      this.FIGMA_L.ACTION_BUBBLE.w,
      this.FIGMA_L.ACTION_BUBBLE.h,
      { fitToSize: true }
    );

    // Logo
    this.logoSprite.scale.set(1);
    this.placeFromFigmaLTWH(
      this.logoSprite as any,
      this.FIGMA_L.LOGO.left,
      this.FIGMA_L.LOGO.top,
      this.FIGMA_L.LOGO.w,
      this.FIGMA_L.LOGO.h,
      { fitToSize: true }
    );

    // Instruction cards
    this.inst1.scale.set(1);
    this.placeFromFigmaLTWH(this.inst1, this.FIGMA_L.TEXTBOX_1.left, this.FIGMA_L.TEXTBOX_1.top, this.FIGMA_L.TEXTBOX_1.w, this.FIGMA_L.TEXTBOX_1.h, {
      fitToSize: true,
    });

    this.inst3.scale.set(1);
    this.placeFromFigmaLTWH(this.inst3, this.FIGMA_L.TEXTBOX_2.left, this.FIGMA_L.TEXTBOX_2.top, this.FIGMA_L.TEXTBOX_2.w, this.FIGMA_L.TEXTBOX_2.h, {
      fitToSize: true,
    });

    this.inst2.scale.set(1);
    this.placeFromFigmaLTWH(this.inst2, this.FIGMA_L.TEXTBOX_3.left, this.FIGMA_L.TEXTBOX_3.top, this.FIGMA_L.TEXTBOX_3.w, this.FIGMA_L.TEXTBOX_3.h, {
      fitToSize: true,
    });

    // Fans: tune (spacing/rot), then fit (scaleMul)
    this.applyFanTune(this.fanLeft, this.FAN_TUNE_L.L);
    this.applyFanTune(this.fanRight, this.FAN_TUNE_L.R);
    this.applyFanTune(this.fanBottom, this.FAN_TUNE_L.B);

    const okL = this.fitContainerToFigmaLTWH(this.fanLeft, this.FIGMA_L.MATCH_CHARACTERS, { scaleMul: this.FAN_TUNE_L.L.scaleMul });
    const okR = this.fitContainerToFigmaLTWH(this.fanRight, this.FIGMA_L.MATCH_BORDERS, { scaleMul: this.FAN_TUNE_L.R.scaleMul });
    const okB = this.fitContainerToFigmaLTWH(this.fanBottom, this.FIGMA_L.THREE_VILLAINS, { scaleMul: this.FAN_TUNE_L.B.scaleMul });

    if (!okL || !okR || !okB) requestAnimationFrame(() => this.requestLayout());

    // CTA
    this.downText.scale.set(1);
    this.placeFromFigmaLTWH(
      this.downText as any,
      this.FIGMA_L.CTA_TEXT.left,
      this.FIGMA_L.CTA_TEXT.top,
      this.FIGMA_L.CTA_TEXT.w,
      this.FIGMA_L.CTA_TEXT.h,
      { fitToSize: false }
    );

    this.designRoot.sortChildren();
  }

  // ---------------------------------------------------------------------------
  // Public hooks (kept for HandleOrientation)
  // ---------------------------------------------------------------------------

  PortraitAdjustFun() {
    this.requestLayout();
  }

  landScapeAdjustFun() {
    this.requestLayout();
  }

  override destroy(options?: boolean | PIXI.DestroyOptions) {
    this.removeLayoutListeners();
    super.destroy(options as any);
  }
}

/**
 * HOW TO ADJUST (quick reference)
 *
 * 1) Fan size (per fan):
 *    - Edit FAN_TUNE_P.L.scaleMul / FAN_TUNE_L.L.scaleMul (etc)
 *      Example: scaleMul: 0.9 makes it 10% smaller than the Figma fit.
 *
 * 2) Spacing between cards:
 *    - Edit FAN_TUNE_*.L.spacingMul
 *      Example: spacingMul: 1.2 spreads cards apart
 *
 * 3) Rotation of each card:
 *    - Edit FAN_TUNE_*.L.cardRotDeg = [-12, 0, 12]
 *
 * 4) Rotation of the whole fan:
 *    - Edit FAN_TUNE_*.L.fanRotDeg
 */
